import React, { useCallback, useEffect, useState } from "react";
import { getOperationsCsrf } from "../operations/operationsApi";
import "./Categories.css";

const BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const blank = { name: "", description: "", display_order: "0", is_active: true };

async function request(url, options = {}) {
  const response = await fetch(`${BASE}${url}`, { credentials: "include", ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data.detail === "string" ? data.detail : Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("; ");
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return data;
}

export default function Categories({ restaurantId }) {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const url = `/api/operations/${restaurantId}/admin/categories/`;

  const reload = useCallback(async () => {
    setLoading(true);
    try { setCategories(await request(url)); setError(""); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [url]);
  useEffect(() => { reload(); }, [reload]);

  function edit(category) {
    setEditing(category);
    setForm({ name: category.name, description: category.description || "", display_order: String(category.display_order), is_active: category.is_active });
    setImage(null); setError(""); setMessage("");
  }
  function reset() { setEditing(null); setForm(blank); setImage(null); }

  async function save(event) {
    event.preventDefault(); setSaving(true); setError(""); setMessage("");
    try {
      const csrf = await getOperationsCsrf();
      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("description", form.description);
      body.append("display_order", String(form.display_order));
      body.append("is_active", form.is_active ? "true" : "false");
      if (image) body.append("image", image);
      await request(editing ? `${url}${editing.id}/` : url, {
        method: editing ? "PATCH" : "POST",
        headers: { "X-CSRFToken": csrf.csrfToken }, body,
      });
      reset(); setMessage(editing ? "Category updated." : "Category created.");
      await reload();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function toggle(category) {
    setError(""); setMessage("");
    try {
      const csrf = await getOperationsCsrf();
      const body = new FormData();
      body.append("is_active", category.is_active ? "false" : "true");
      await request(`${url}${category.id}/`, { method: "PATCH", headers: { "X-CSRFToken": csrf.csrfToken }, body });
      setMessage(`${category.name} ${category.is_active ? "disabled" : "enabled"}.`);
      await reload();
    } catch (err) { setError(err.message); }
  }

  return <div className="ra-categories">
    <div className="rac-heading"><div><h2>Menu categories</h2><p>Manage the categories displayed in your restaurant's menu.</p></div><strong>{categories.length} categories</strong></div>
    {error && <p className="rac-alert rac-error" role="alert">{error}</p>}
    {message && <p className="rac-alert rac-success" role="status">{message}</p>}
    <div className="rac-layout">
      <section className="rac-panel"><h3>{editing ? `Edit category #${editing.id}` : "Add a category"}</h3>
        <form onSubmit={save}>
          <label>Name <input required maxLength={100} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Starters" /></label>
          <label>Description <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}/></label>
          <label>Display order <input type="number" min="0" step="1" required value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })}/></label>
          <label>Category image (PNG, JPEG, WebP; max 2 MB) <input type="file" accept="image/png,image/jpeg,image/webp" onChange={e => setImage(e.target.files?.[0] || null)}/></label>
          {editing?.image_url && <img className="rac-preview" src={editing.image_url} alt="Current category"/>}
          <label className="rac-check"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })}/> Active</label>
          <div className="rac-actions"><button type="submit" disabled={saving}>{saving ? "Saving…" : editing ? "Save changes" : "Create category"}</button>{editing && <button type="button" className="rac-secondary" onClick={reset}>Cancel</button>}</div>
        </form>
      </section>
      <section className="rac-panel"><h3>Existing categories</h3>
        {loading ? <p>Loading categories…</p> : categories.length === 0 ? <p>No categories yet. Create your first category.</p> :
          <div className="rac-list">{categories.map(category => <article className="rac-item" key={category.id}>
            {category.image_url ? <img src={category.image_url} alt=""/> : <div className="rac-placeholder">No image</div>}
            <div className="rac-details"><strong>{category.name}</strong><p>{category.description || "No description"}</p><small>Order: {category.display_order} · {category.item_count} items · {category.is_active ? "Active" : "Inactive"}</small></div>
            <div className="rac-item-actions"><button type="button" onClick={() => edit(category)}>Edit</button><button type="button" className="rac-secondary" onClick={() => toggle(category)}>{category.is_active ? "Disable" : "Enable"}</button></div>
          </article>)}</div>}
      </section>
    </div>
  </div>;
}
